import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import storeRequest from '../../../requests/api/store';
import handleApiResponse from '../../../utils/handleApiResponse';
import { parseUserType } from '../../../utils/const/user_types';
import useStyles from '../EditStoreView/styles';
import CustomAutocomplete from '../../../components/Inputs/Autocomplete/CustomAutocomplete';
import CustomTextField from '../../../components/Inputs/TextField/CustomTextField';

function UsersAssociation({
  users,
  totalUsers,
  storeId,
  initialSelectedUserIds,
  page,
  limit,
  setPage,
  setLimit,
  setSearchValue,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [selectedUsers, setSelectedUsers] = useState(initialSelectedUserIds);
  const [searchText, setSearchText] = useState('');

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(users.map((u) => Number(u.id)));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectOne = (userId) => {
    const id = Number(userId);
    if (selectedUsers.includes(id)) {
      setSelectedUsers((prev) => prev.filter((sid) => sid !== id));
    } else {
      setSelectedUsers((prev) => [...prev, id]);
    }
  };

  const handleSave = async () => {
    try {
      const response = await storeRequest.putUserStoreFromStore({
        userIds: selectedUsers,
        storeId: parseInt(storeId, 10),
      });
      handleApiResponse(enqueueSnackbar, response, true);
      history.push('/administracion/tiendas');
    } catch (err) {
      handleApiResponse(enqueueSnackbar, err, false);
    }
  };

  const selectedSome = selectedUsers.length > 0 && selectedUsers.length < users.length;
  const selectedAll = users.length > 0 && selectedUsers.length === users.length;

  return (
    <Box>
      <Paper elevation={3}>
        <Box
          display="flex"
          alignItems="center"
          padding="15px 10px"
          justifyContent="space-between"
        >
          <Box flex={1} mr={4}>
            <CustomAutocomplete
              disablePortal
              isOptionEqualToValue={(option, value) => value && option.name === value.name}
              id="user-search"
              fullWidth
              noOptionsText="Sin usuarios para mostrar"
              options={users.map((u) => ({
                id: u.id,
                name: `${u.name} ${u.lastname}`,
                email: u.email,
                role: u.role,
              }))}
              filterSelectedOptions
              inputValue={searchText}
              value={null}
              getOptionLabel={(option) => `${option.name} - ${option.email} - ${parseUserType(option.role)}`}
              onChange={(e, newValue) => {
                if (newValue) handleSelectOne(newValue.id);
                setSearchText('');
                setSearchValue('');
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Buscar usuarios"
                  variant="outlined"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              )}
            />
          </Box>
        </Box>
      </Paper>
      <Box mt={3}>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAll}
                    indeterminate={selectedSome}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Correo</TableCell>
                <TableCell>Rol</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(Number(user.id))}
                      onChange={() => handleSelectOne(user.id)}
                    />
                  </TableCell>
                  <TableCell>{`${user.name} ${user.lastname}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{parseUserType(user.role)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box mt={2} className={classes.tablePagination}>
            <TablePagination
              component="div"
              count={totalUsers}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setLimit(e.target.value); setPage(0); }}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage="Filas por página"
            />
            <Box>
              <Button
                variant="contained"
                color="primary"
                className={classes.orangeButton}
                onClick={handleSave}
              >
                Guardar
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

UsersAssociation.propTypes = {
  users: PropTypes.array.isRequired,
  totalUsers: PropTypes.number.isRequired,
  storeId: PropTypes.string.isRequired,
  initialSelectedUserIds: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setLimit: PropTypes.func.isRequired,
  setSearchValue: PropTypes.func.isRequired,
};

export default UsersAssociation;
